import { z } from 'zod';
import {
  artifactKindSchema,
  safeResourceUrlSchema,
} from './artifact.ts';
import { jsonObjectSchema } from './json.ts';

export const assetLicenseSchema = z.object({
  name: z.string().min(1).max(500),
  usage: z.enum([
    'allowed',
    'attribution-required',
    'internal-only',
    'restricted',
    'unknown',
  ]),
  author: z.string().max(500).optional(),
  sourceUrl: safeResourceUrlSchema.optional(),
  attribution: z.string().max(2000).optional(),
  notes: z.string().max(5000).optional(),
}).strict().superRefine((license, context) => {
  if (
    license.usage === 'attribution-required'
    && !license.attribution?.trim()
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['attribution'],
      message: '要求署名的资源必须提供 attribution',
    });
  }
});

export const assetAspectRatioSchema = z.object({
  width: z.number().positive().finite().max(100000),
  height: z.number().positive().finite().max(100000),
  tolerance: z.number().nonnegative().max(1).default(0.1),
}).strict();

export const assetAiGuidanceSchema = z.object({
  summary: z.string().min(1).max(5000),
  useWhen: z.array(z.string().min(1).max(1000)).max(100).default([]),
  avoidWhen: z.array(z.string().min(1).max(1000)).max(100).default([]),
  placementHints: z.array(z.string().min(1).max(1000)).max(100).default([]),
}).strict();

export const assetDescriptorSchema = z.object({
  key: z.string().regex(
    /^[a-z0-9][a-z0-9-]*(?:\.[a-z0-9][a-z0-9-]*)+@\d+$/,
    '资源键必须使用 namespace.name@version 格式',
  ),
  title: z.string().min(1).max(500),
  description: z.string().max(5000).default(''),
  kind: artifactKindSchema,
  sourceUrl: safeResourceUrlSchema.optional(),
  mimeType: z.string().min(1).max(200).optional(),
  semanticTags: z.array(z.string().min(1).max(200)).max(200).default([]),
  applicableScenes: z.array(z.string().min(1).max(500)).max(100).default([]),
  visualStyles: z.array(z.string().min(1).max(200)).max(100).default([]),
  states: z.array(z.string().min(1).max(200)).max(100).default([]),
  actions: z.array(z.string().min(1).max(200)).max(100).default([]),
  aspectRatio: assetAspectRatioSchema,
  replacements: z.array(z.string().min(1).max(500)).max(100).default([]),
  license: assetLicenseSchema,
  aiGuidance: assetAiGuidanceSchema,
  metadata: jsonObjectSchema.default({}),
}).strict().superRefine((asset, context) => {
  for (const field of [
    'semanticTags',
    'applicableScenes',
    'visualStyles',
    'states',
    'actions',
    'replacements',
  ] as const) {
    const seen = new Set<string>();
    for (const [index, value] of asset[field].entries()) {
      if (seen.has(value)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: [field, index],
          message: `资源描述存在重复项：${value}`,
        });
      }
      seen.add(value);
    }
  }
  if (asset.replacements.includes(asset.key)) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['replacements'],
      message: '资源不能把自身声明为可替换资源',
    });
  }
});

export const assetCatalogSchema = z.object({
  schemaVersion: z.literal('1.0.0'),
  revision: z.number().int().nonnegative().default(0),
  assets: z.array(assetDescriptorSchema).max(100000).default([]),
}).strict().superRefine((catalog, context) => {
  const keys = new Set<string>();
  for (const [index, asset] of catalog.assets.entries()) {
    if (keys.has(asset.key)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['assets', index, 'key'],
        message: `资源键重复：${asset.key}`,
      });
    }
    keys.add(asset.key);
  }
  for (const [assetIndex, asset] of catalog.assets.entries()) {
    for (const [replacementIndex, replacement] of asset.replacements.entries()) {
      if (!keys.has(replacement)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['assets', assetIndex, 'replacements', replacementIndex],
          message: `可替换资源未在目录中注册：${replacement}`,
        });
      }
    }
  }
});

export type AssetLicense = z.infer<typeof assetLicenseSchema>;
export type AssetAspectRatio = z.infer<typeof assetAspectRatioSchema>;
export type AssetAiGuidance = z.infer<typeof assetAiGuidanceSchema>;
export type AssetDescriptor = z.infer<typeof assetDescriptorSchema>;
export type AssetCatalog = z.infer<typeof assetCatalogSchema>;
